import { useEffect, useState, useRef } from "react";
import { Upload, Trash2, Copy, Download, Globe, Lock, Video, FileText, File as FileIcon, Image as ImageIcon, Loader2, Search } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CHUNK = 5 * 1024 * 1024;
const SIMPLE_MAX = 9 * 1024 * 1024;
const CATS = [["", "All"], ["image", "Images"], ["video", "Videos"], ["document", "Documents"], ["other", "Other"]];
const fmt = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round((b || 0) / 1024))} KB`);
const publicUrl = (f) => `${BACKEND}/api/files/${f.storage_path}`;

function MediaThumb({ file }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    if (file.category === "image") {
      api.get(`/files/${file.storage_path}`, { responseType: "blob" })
        .then((r) => { url = URL.createObjectURL(r.data); setSrc(url); }).catch(() => {});
    }
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [file.storage_path, file.category]);
  if (file.category === "image") {
    return src ? <img src={src} alt={file.original_filename} className="w-full h-full object-cover" />
      : <div className="w-full h-full bg-slate-100 animate-pulse" />;
  }
  const Icon = file.category === "video" ? Video : file.category === "document" ? FileText : FileIcon;
  return <div className="w-full h-full flex items-center justify-center bg-slate-100 text-slate-400"><Icon size={38} /></div>;
}

export default function AdminMedia() {
  const [files, setFiles] = useState([]);
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");
  const [vis, setVis] = useState("public");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressName, setProgressName] = useState("");
  const inputRef = useRef(null);

  const load = () => {
    const params = new URLSearchParams();
    if (cat) params.set("category", cat);
    if (q) params.set("q", q);
    api.get(`/admin/files?${params.toString()}`).then((r) => setFiles(r.data)).catch((e) => toast.error(apiErr(e)));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cat]);

  const uploadOne = async (file) => {
    setProgressName(file.name);
    setProgress(0);
    if (file.size <= SIMPLE_MAX) {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/files/upload?visibility=${vis}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => setProgress(Math.round((e.loaded / (e.total || file.size)) * 100)),
      });
      return;
    }
    const { data: init } = await api.post("/admin/files/upload/init", {
      filename: file.name, content_type: file.type, size: file.size, visibility: vis,
    });
    let index = 0;
    for (let start = 0; start < file.size; start += CHUNK) {
      const blob = file.slice(start, Math.min(start + CHUNK, file.size));
      await api.post("/admin/files/upload/chunk", blob, {
        headers: { "X-Upload-Id": init.upload_id, "X-Chunk-Index": String(index), "Content-Type": "application/octet-stream" },
      });
      index++;
      setProgress(Math.round((Math.min(start + CHUNK, file.size) / file.size) * 100));
    }
    await api.post("/admin/files/upload/complete", { upload_id: init.upload_id });
  };

  const onPick = async (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    setBusy(true);
    try {
      for (const f of picked) await uploadOne(f);
      toast.success(`${picked.length} file(s) uploaded`);
      load();
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false); setProgress(0); setProgressName("");
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const toggleVis = async (f) => {
    try {
      await api.patch(`/admin/files/${f.id}`, { visibility: f.visibility === "public" ? "private" : "public" });
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (f) => {
    if (!window.confirm(`Delete "${f.original_filename}"?`)) return;
    try { await api.delete(`/admin/files/${f.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const copyLink = (f) => { navigator.clipboard.writeText(publicUrl(f)); toast.success("Public link copied"); };
  const download = async (f) => {
    try {
      const r = await api.get(`/files/${f.storage_path}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = f.original_filename || "file";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const shown = files.filter((f) => !q || (f.original_filename || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="admin-media-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-extrabold text-slate-900">Media Library</h1>
          <p className="text-sm text-slate-500">Images, videos, documents & files — public link ya private (staff-only).</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={vis} onChange={(e) => setVis(e.target.value)} data-testid="upload-visibility"
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white">
            <option value="public">Public (shareable)</option>
            <option value="private">Private (staff only)</option>
          </select>
          <input ref={inputRef} type="file" multiple className="hidden" onChange={onPick} data-testid="media-file-input" />
          <button onClick={() => inputRef.current?.click()} disabled={busy} data-testid="media-upload-btn"
            className="inline-flex items-center gap-2 bg-emerald-600 text-white font-bold px-4 py-2 rounded-lg hover:bg-emerald-700 disabled:opacity-60">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Upload
          </button>
        </div>
      </div>

      {busy && (
        <div className="bg-white border border-slate-200 rounded-xl p-3" data-testid="media-upload-progress">
          <div className="flex justify-between text-xs text-slate-600 mb-1">
            <span className="truncate">Uploading {progressName}…</span><span>{progress}%</span>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {CATS.map(([v, l]) => (
          <button key={v || "all"} onClick={() => setCat(v)} data-testid={`media-cat-${v || "all"}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${cat === v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>{l}</button>
        ))}
        <div className="relative ml-auto min-w-0">
          <Search size={15} className="absolute left-2.5 top-2.5 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search files…" data-testid="media-search"
            className="pl-8 pr-3 py-2 border border-slate-200 rounded-lg text-sm w-full sm:w-56" />
        </div>
      </div>

      {shown.length === 0 ? (
        <div className="text-center py-16 text-slate-400" data-testid="media-empty">Koi file nahi. Upload karke shuru karein.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="media-grid">
          {shown.map((f) => (
            <div key={f.id} data-testid={`media-card-${f.id}`} className="bg-white border border-slate-200 rounded-xl overflow-hidden group">
              <div className="aspect-square relative"><MediaThumb file={f} />
                <span className={`absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${f.visibility === "public" ? "bg-emerald-100 text-emerald-700" : "bg-slate-800 text-white"}`}>
                  {f.visibility === "public" ? <Globe size={10} /> : <Lock size={10} />} {f.visibility}
                </span>
              </div>
              <div className="p-2.5 space-y-2">
                <div className="text-xs font-semibold text-slate-800 truncate" title={f.original_filename}>{f.original_filename}</div>
                <div className="text-[10px] text-slate-400 uppercase">{f.category} · {fmt(f.size)}</div>
                <div className="flex items-center gap-1">
                  <button onClick={() => toggleVis(f)} title="Toggle visibility" data-testid={`media-vis-${f.id}`}
                    className="flex-1 text-[11px] font-semibold px-2 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700">
                    {f.visibility === "public" ? "Make Private" : "Make Public"}
                  </button>
                </div>
                <div className="flex items-center gap-1">
                  {f.visibility === "public" && (
                    <button onClick={() => copyLink(f)} title="Copy public link" data-testid={`media-copy-${f.id}`}
                      className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><Copy size={15} /></button>
                  )}
                  <button onClick={() => download(f)} title="Download" data-testid={`media-download-${f.id}`}
                    className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><Download size={15} /></button>
                  <button onClick={() => del(f)} title="Delete" data-testid={`media-delete-${f.id}`}
                    className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 ml-auto"><Trash2 size={15} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
